import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { FirebaseApp } from '@angular/fire';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { RegionInfo, XpoLtlAuthenticationService } from '@xpo-ltl/ngx-auth';
import { Envelope } from '@xpo-ltl/sdk-common';
import { XpoAuthenticationService } from '@xpo/ngx-auth';
import firebase from 'firebase/app';
import 'firebase/auth';
import { defer, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ConfigManagerProperties } from '../enums/config-manager-properties.enum';

/**
 * TODO: Remove this file after migration to newer Angular version
 *
 * This is a temporary patch for the default XpoLtlAuthenticationService auth flow.
 * Once the project is migrated to a never Angular version (and removed half of the unnessary dependencies),
 * these files in this folder can be removed.
 *
 * Out of the box, the auth setup only authenticates against OIDC. It then goes straight to
 * fetching the secrets that the interceptor needs to create the API token. Those secrets are
 * read from Firebase Frestore. Soon Firestore will get Firestore Rules applied, which blocks this
 * sdk call unless authenticated with Firebase Auth
 *
 * This service makes sure the user is authenticated with BOTH OIDC and Firebase before the
 * auth setup continues to the token call:
 * - `initAuthSetupWithLogin$` checks for a Firebase session. If there is none, it exchanges the
 *   OIDC access token for a Firebase custom token, signs in with it, and only then runs the
 *   regular `initAuthSetup$`.
 * - `canActivate` blocks routing until a Firebase user is available.
 *
 * The custom token is fetched through `HttpBackend`, which bypasses the HTTP interceptors.
 * Going through the interceptor would require the API token, and that token depends on
 * Firebase auth, so the request could never complete.
 */
@Injectable({
  providedIn: 'root',
})
export class OsdXpoLtlAuthenticationService extends XpoLtlAuthenticationService implements CanActivate {
  private osdAuthConfigService = inject(OsdAuthConfigService);
  private backendHttp = inject(HttpBackend);
  private http = new HttpClient(this.backendHttp);

  private fetchFirebaseToken$ = defer(() => {
    return this.http.post<Envelope<{ authToken: string }>>(this.osdAuthConfigService.firebaseTokenUrl, this.osdAuthConfigService.envParams);
  }).pipe(
    map(res => {
      return res.data.authToken;
    }),
    catchError(error => {
      console.warn('Could not retrieve Firebase token', error);
      return of('');
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    })
  );

  readonly isOwner$ = this.osdAuthConfigService.firebaseUser$.pipe(
    map(firebaseUser => {
      const firebaseUserUid = firebaseUser?.uid || firebaseUser?.email || '';
      const savedUser = { ...this.osdAuthConfigService.savedOidcUser };
      const oidcUserUid = savedUser.upn || savedUser.email || (savedUser.sub || '').split(':')[0] || '';
      const isOwner = !!firebaseUserUid && !!oidcUserUid && firebaseUserUid.toLowerCase() === oidcUserUid.toLowerCase();
      return isOwner;
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    })
  );

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.isOwner$.pipe(
      filter(u => {
        return !!u;
      }),
      take(1)
    );
  }

  initAuthSetupWithLogin$(): Observable<RegionInfo | null> {
    return this.isOwner$.pipe(
      take(1),
      switchMap(isOwner => {
        const { region } = this.osdAuthConfigService.envParams;
        if (isOwner) {
          return super.initAuthSetup$(region);
        }

        // Scenario B: Unauthenticated -> fetch custom token -> sign in -> setup
        // AngularFirestore shares the same FirebaseApp, so its requests carry the signed-in user's ID token
        return this.fetchFirebaseToken$.pipe(
          switchMap(token => {
            if (!token) return of(null);

            return defer(() => this.osdAuthConfigService.firebaseAuth.signInWithCustomToken(token)).pipe(
              switchMap(() => {
                return super.initAuthSetup$(region);
              }),
              take(1)
            );
          })
        );
      })
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class OsdXpoAuthenticationService extends XpoAuthenticationService {
  private osdAuthConfigService = inject(OsdAuthConfigService);

  signOut$(): Observable<void> {
    return this.signOutFirebase$().pipe(
      switchMap(() => super.signOut$()),
      take(1)
    );
  }

  signOutFirebase$() {
    return defer(() => this.osdAuthConfigService.firebaseAuth.signOut());
  }
}

@Injectable({
  providedIn: 'root',
})
export class OsdAuthConfigService {
  private configManagerService = inject(ConfigManagerService);
  private injector = inject(Injector);

  get firebaseApp() {
    const appName = this.envParams.appName;
    const apps: FirebaseApp[] = firebase.apps;
    const firebaseApp = apps.find(app => app.name === appName) || apps[0] || firebase.initializeApp(environment.firebase);
    const injectedApp = this.injector.get(FirebaseApp, undefined);
    return injectedApp || firebaseApp;
  }

  get firebaseAuth() {
    const auth = this.firebaseApp.auth();
    return auth;
  }

  get firebaseUser$() {
    const auth = this.firebaseAuth;
    return new Observable<firebase.User | null>(subscriber => {
      const unsubscribe = auth.onAuthStateChanged(
        user => {
          subscriber.next(user);
        },
        error => {
          subscriber.error(error);
        },
        () => {
          subscriber.complete();
        }
      );

      // Teardown logic: Unsubscribe when the RxJS subscription ends
      return () => unsubscribe();
    });
  }

  private get xpoAccessTokenByParams() {
    const searchParams = this.searchParams;
    const access_token_params = searchParams?.get('access_token') || '';
    return access_token_params;
  }

  get envParams() {
    const envParams = {
      appName: (this.configManagerService.getSetting<string>(ConfigManagerProperties.appName) || '').trim().toLowerCase(),
      region: (this.configManagerService.getSetting<string>(ConfigManagerProperties.region) || '').trim().toUpperCase(),
      assertionToken: this.xpoAccessTokenByParams || this.savedOidcUser?.access_token || '',
    };
    return envParams;
  }

  get savedOidcUser() {
    /**
     * This would usually be derived from the oidc client (getUser and getUser$) but
     * it is not properly initiated and exposed so this is the hacky way
     */
    try {
      const authAppCode = this.configManagerService.getSetting<string>('authAppCode');
      for (const [key, value] of Object.entries(sessionStorage)) {
        if (value && key && key.includes('oidc') && key.includes(authAppCode) && value.includes('access_token')) {
          const user = JSON.parse(value);
          return {
            email: user.email || user.profile?.email || '',
            access_token: user.access_token || '',
            sub: user.profile?.sub || '',
            upn: user.profile?.upn || '',
          };
        }
      }
    } catch (error) {
      console.warn('Could not derive the user from storage', error);
    }

    // On the OIDC callback the user is not stored yet, so decode it from the access token in the url.
    // Must not use envParams here: envParams falls back to savedOidcUser, which would recurse forever.
    const access_token = this.xpoAccessTokenByParams;
    if (!access_token) {
      return null;
    }
    try {
      const payload = JSON.parse(
        atob(
          access_token
            .split('.')[1]
            .replace(/-/g, '+')
            .replace(/_/g, '/')
        )
      );
      return {
        email: payload.email || '',
        access_token,
        sub: payload.sub || '',
        upn: payload.upn || '',
      };
    } catch (error) {
      console.warn('Could not derive the user from the url token', error);
    }
    return null;
  }

  get firebaseTokenUrl() {
    const apiUrl = this.configManagerService.getSetting<string>('apiUrl');
    const url = new URL('firebase/1.0/token', apiUrl).toString();
    return url;
  }

  private get searchParams() {
    try {
      const searchParamsPart = window.location.href.split('auth#')?.[1] || '';
      const searchParams = new URLSearchParams(searchParamsPart);
      return searchParams;
    } catch (error) {
      console.warn('Could not retrieve token from url');
    }
    return null;
  }
}
